import * as React from 'react';
import { useContext } from 'react';

import type {
  PretranslatedMap,
  PretranslatedType,
} from 'bundles/coursera-plus-landing-page/components/ctaContentful/Pretranslated';
import { getPretranslatedMessages } from 'bundles/coursera-plus-landing-page/components/ctaContentful/Pretranslated';
import type { PickedLandingForTool } from 'bundles/coursera-plus-landing-page/contentful/fetchContentful';
import CourseraPlusProductVariant from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';

export type Pretranslated = {
  translationKey: string;
  translation: PretranslatedType;
  pages: PickedLandingForTool[];
};

export type ImageReference = {
  url: string;
  id: string;
  pages: PickedLandingForTool[];
};

export type FeaturesContextType = {
  codedKeys: string[];
  codedMap: PretranslatedMap;
  pages: PickedLandingForTool[];
  pretranslatedFromCTA: Record<string, Pretranslated>;
  images: ImageReference[];
};

export const FeaturesContext = React.createContext<FeaturesContextType | undefined>(undefined);

export const useFeaturesContext = () => {
  return useContext(FeaturesContext);
};

export const createFeaturesContextValue = (pages: PickedLandingForTool[]): FeaturesContextType => {
  const codedTranslations = getPretranslatedMessages(CourseraPlusProductVariant.MONTHLY_WITH_FREE_TRIAL);
  const pretranslatedFromCTAEntries: Record<string, Pretranslated> = {};
  const images: ImageReference[] = [];
  for (const page of pages) {
    // extract pretranslated references
    const collection = page.heroCtaPartsCollection;
    if (collection) {
      for (const item of collection.items) {
        if (item && item.pretranslated) {
          const s = pretranslatedFromCTAEntries[item.pretranslated];
          if (s) {
            s.pages.push(page);
          } else {
            pretranslatedFromCTAEntries[item.pretranslated] = {
              translationKey: item.pretranslated,
              pages: [page],
              translation: codedTranslations[item.pretranslated],
            };
          }
        }
      }
    }
    // extract image references
    const heroImage = page.heroImage;
    if (heroImage && heroImage.url) {
      const imageListed = images.find((i) => i.id === heroImage.sys.id);
      if (imageListed) {
        imageListed.pages.push(page);
      } else {
        images.push({
          url: heroImage.url,
          id: heroImage.sys.id,
          pages: [page],
        });
      }
    }
  }

  return {
    codedKeys: Object.keys(codedTranslations),
    codedMap: codedTranslations,
    pages,
    pretranslatedFromCTA: pretranslatedFromCTAEntries,
    images,
  };
};
